import * as React from 'react';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ReportIcon from '@mui/icons-material/Assessment';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DataSetIcon from '@mui/icons-material/DataObject';
import PreviewIcon from '@mui/icons-material/Preview';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import SpeechIcon from '@mui/icons-material/RecordVoiceOver';
import FaceIcon from '@mui/icons-material/PhotoCameraFront';
import ArmsIcon from '@mui/icons-material/Accessibility';
import HospitalIcon from '@mui/icons-material/LocalHospital';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { NavItem } from './NavItem';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import axios from '../util/Axios';
import { Collapse, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ConsentIcon from '@mui/icons-material/AssignmentInd';

const drawerWidth = 240;

const patientSection = [{ name: 'patients', path: '/myTests', title: 'My Tests', icon: <PersonIcon /> }];

const doctorsSection = [
  { name: 'profile', path: '/profile', title: 'My Profile', icon: <PersonIcon /> },
  { name: 'patients', path: '/patients', title: 'My Patients', icon: <GroupIcon /> },
];

const adminSection = [
  { name: 'doctors', path: '/doctors', title: 'Doctors', icon: <GroupIcon /> },
  { name: 'hospitals', path: '/hospitals', title: 'Hospitals', icon: <HospitalIcon /> },
  { name: 'invitations', path: '/invitations', title: 'Invitations', icon: <GroupIcon /> },
  { name: 'reports', path: '/reports', title: 'Doctors Activity', icon: <ReportIcon /> },
  { name: 'deletedTests', path: '/deletedTests', title: 'Deleted Tests', icon: <DeleteIcon /> },
];

const legalSection = [{ name: 'informedConsents', path: '/informedConsents', title: 'Informed Consents', icon: <ConsentIcon /> }];

const allTestsNav = { name: 'alltests', path: '/all', title: 'All Tests', icon: <SummarizeIcon /> };
const testsForValidityReview = { name: 'forValidityReview', path: '/forValidityReview', title: 'Tests for Validity Review', icon: <PreviewIcon /> };
const testsForMedicalReview = { name: 'forMedicalReview', path: '/forMedicalReview', title: 'Tests for Medical Review', icon: <PreviewIcon /> };
const validTestsItems = [
  { name: 'validFace', path: '/validFace', title: 'Valid Face Tests', icon: <SummarizeIcon /> },
  { name: 'validSpeech', path: '/validSpeech', title: 'Valid Speech Tests', icon: <SummarizeIcon /> },
  { name: 'validArms', path: '/validArms', title: 'Valid Arms Tests', icon: <SummarizeIcon /> },
];
const icons = {
  ARMS: <ArmsIcon />,
  SPEECH: <SpeechIcon />,
  FACE: <FaceIcon />,
};

export default function Menu({ mobileOpen, drawerToggle, window }) {
  const isAdmin = useSelector((state) => state.GlobalReducer.loggedUser?.adminRole === 'ADMIN');
  const isLegalAdmin = useSelector((state) => state.GlobalReducer.loggedUser?.adminRole === 'LEGAL_TEAM');
  const isPatient = useSelector((state) => state.GlobalReducer.loggedUser?.role === 'PATIENT');
  const container = window !== undefined ? () => window().document.body : undefined;
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.only('xs'));
  const [dataSetMenuItems, setDataSetMenuItems] = React.useState([]);
  const [dataSetArchivedMenuItems, setDataSetArchivedMenuItems] = React.useState([]);
  const refreshDataSets = useSelector((state) => state.GlobalReducer.refreshDataSets);
  const [open, setOpen] = React.useState(false);
  const [openArchived, setOpenArchived] = React.useState(false);

  const getPersonalSection = () => {
    if (isLegalAdmin) return legalSection;
    if (isPatient) return patientSection;

    return doctorsSection;
  };

  const handleToggleDatasets = () => {
    setOpen(!open);
  };

  const handleToggleArchivedDatasets = () => {
    setOpenArchived(!openArchived);
  };

  React.useEffect(() => {
    if (!isAdmin) return;
    
    axios(`/dataSets?size=2000&sort=name`).then((response) => {
      const dsMenus = response.data.content.map((ds) => ({ name: ds.name, title: ds.name, path: `/dataSets/${ds.uuid}`, icon: icons[ds.dataSetType], archived: ds.archived }));
      setDataSetMenuItems(dsMenus.filter((ds) => !ds.archived));
      setDataSetArchivedMenuItems(dsMenus.filter((ds) => ds.archived));
    });
  }, [refreshDataSets, isAdmin]);

  const menuList = (
    <List sx={{ marginTop: '50px' }}>
      {getPersonalSection().map((nav) => (
        <NavItem key={nav.name} nav={nav} onNavigate={drawerToggle} />
      ))}

      <Divider />
      {isAdmin ? (
        <>
          <Divider />
          {legalSection.map((nav) => (
            <NavItem key={nav.name} nav={nav} onNavigate={drawerToggle} />
          ))}
          <Divider />
          {adminSection.map((nav) => (
            <NavItem key={nav.name} nav={nav} onNavigate={drawerToggle} />
          ))}
          <Divider />
          <NavItem nav={testsForValidityReview} onNavigate={drawerToggle} />
          <NavItem nav={testsForMedicalReview} onNavigate={drawerToggle} />
          <NavItem nav={allTestsNav} onNavigate={drawerToggle} />
          {validTestsItems.map((nav) => (
            <NavItem key={nav.name} nav={nav} onNavigate={drawerToggle} />
          ))}
          <Divider />
          <ListItemButton onClick={handleToggleDatasets}>
            <ListItemIcon>
              <DataSetIcon />
            </ListItemIcon>
            <ListItemText primary="Datasets" />
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {dataSetMenuItems.map((nav) => (
                <NavItem key={nav.name} nav={nav} onNavigate={drawerToggle} />
              ))}
            </List>
          </Collapse>
          <Divider />
          <ListItemButton onClick={handleToggleArchivedDatasets}>
            <ListItemIcon>
              <DataSetIcon />
            </ListItemIcon>
            <ListItemText primary="Archived" />
            {openArchived ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={openArchived} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {dataSetArchivedMenuItems.map((nav) => (
                <NavItem key={nav.name} nav={nav} onNavigate={drawerToggle} />
              ))}
            </List>
          </Collapse>
        </>
      ) : (
        ''
      )}
    </List>
  );

  return (
    <>
      <Drawer
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        {menuList}
      </Drawer>
      <Drawer
        sx={{
          display: { xs: 'block', sm: 'none' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="temporary"
        open={mobileOpen && xs}
        onClose={drawerToggle}
        anchor="left"
        ModalProps={{
          keepMounted: false,
        }}
        container={container}
      >
        {menuList}
      </Drawer>
    </>
  );
}
